import '../../assets/css/case-property.css';
import cornerIcon from '../../assets/images/case-image-corner.svg';
// @ts-ignore
import { useProperty, useContentToken, EditableToken, useSlotGroup, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
// @ts-ignore
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWeightScale, faShieldCat, faMoneyCheckDollar } from '@fortawesome/free-solid-svg-icons';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { getPropertyBasePath, phoneDigits } from './propertyUtils';
import { useFilteredGalleryIndices } from './useFilteredGallery';

function createImageDef(label: string, defaultIndex: number): SlotGroupDefinition {
  return {
    label,
    fields: {
      image: { label: 'Image', type: 'image', defaultField: `photo_gallery[${defaultIndex}].image` },
      alt: { label: 'Alt Text', type: 'text', defaultField: `photo_gallery[${defaultIndex}].alt_text` },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex,
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  };
}

export const PET_SLOT_DEFS: Record<string, SlotGroupDefinition> = {
  'petpolicy.pet_image': createImageDef('Pet Section Image', 0),
};

/** True if value looks like a raw number (no $, no descriptive text) */
function isRawNumber(val: string | null | undefined): boolean {
  if (val == null || val === '') return false;
  const trimmed = String(val).trim();
  return /^\d+(\.\d+)?$/.test(trimmed) && !trimmed.includes('$') && !trimmed.toLowerCase().includes('per pet');
}

/** CMS sometimes sends 0 / "false" when amounts are unknown — don't show those as real prices. */
function isUnknownPetFeeOrRent(raw: string | null | undefined): boolean {
  if (raw == null) return true;
  const s = String(raw).trim();
  if (s === '') return true;
  if (s.toLowerCase() === 'false') return true;
  if (!isRawNumber(s)) return false;
  const num = parseFloat(s);
  return num === 0;
}

const PET_RENT_AND_FEE_UNKNOWN_MESSAGE =
  'Please contact us for more information on pet fees and pet rent.*';
const PET_RENT_UNKNOWN_MESSAGE = 'Please contact us for more information on pet rent.*';
const PET_FEE_UNKNOWN_MESSAGE = 'Please contact us for more information on pet fees.*';

/** Pet rent / fee lines stack; avoid repeating the same “contact us” copy when both are unknown. */
function formatPetMoneyLine(
  raw: string | null | undefined,
  type: 'pet_rent' | 'pet_fee',
  otherRaw: string | null | undefined
): string {
  const selfUnknown = isUnknownPetFeeOrRent(raw);
  const otherUnknown = isUnknownPetFeeOrRent(otherRaw);
  if (selfUnknown && otherUnknown) {
    return type === 'pet_rent' ? PET_RENT_AND_FEE_UNKNOWN_MESSAGE : '';
  }
  if (selfUnknown) {
    return type === 'pet_rent' ? PET_RENT_UNKNOWN_MESSAGE : PET_FEE_UNKNOWN_MESSAGE;
  }
  return formatPetPolicyValue(raw, type);
}

/** Format raw numeric values into user-friendly display strings for pet policy fields. */
function formatPetPolicyValue(
  raw: string | null | undefined,
  type: 'weight_limit' | 'max_pets' | 'pet_rent' | 'pet_fee' | 'pet_deposit'
): string {
  const str = raw == null ? '' : String(raw).trim();
  if (!str) {
    if (type === 'weight_limit') return 'No weight limit';
    if (type === 'pet_deposit') return '';
    return str;
  }
  if (!isRawNumber(str)) return str;

  const num = parseFloat(str);
  switch (type) {
    case 'weight_limit':
      return num === 0 || Number.isNaN(num) ? 'No weight limit' : `${num} lbs max`;
    case 'max_pets':
      return num === 1 ? 'One pet allowed' : num === 0 ? 'No pets allowed' : `Maximum of ${num} pets allowed`;
    case 'pet_rent':
      return `Pet rent $${num} (per pet per month)*`;
    case 'pet_fee':
      return `$${num} fee per pet (non-refundable)*`;
    case 'pet_deposit':
      return num === 0 ? '' : `$${num} deposit per pet`;
    default:
      return str;
  }
}

export function PetPolicy() {
  const property = useProperty();
  const name = property.property_name || '';
  const shortName = name.replace(/\s*Apartments?\s*$/i, '');
  const basePath = getPropertyBasePath(property);
  const phone = property.property_call_tracking_phone || property.property_phone || '';

  const weightLimit = useContentToken('petpolicy.weight_limit');
  const maxPets = useContentToken('petpolicy.max_pets');
  const breedNote = useContentToken('petpolicy.breed_note');
  const petRent = useContentToken('petpolicy.pet_rent');
  const petFee = useContentToken('petpolicy.pet_fee');
  const petDeposit = useContentToken('petpolicy.pet_deposit');

  const fi = useFilteredGalleryIndices();
  const petImage = useSlotGroup('petpolicy.pet_image', createImageDef('Pet Section Image', fi[0] ?? 0));

  const petRentLine = formatPetMoneyLine(petRent, 'pet_rent', petFee);
  const petFeeLine = formatPetMoneyLine(petFee, 'pet_fee', petRent);

  return (
    <>
      {/* ── Pet Image (full-width, dark bg) ── */}
      <section className="prop-al-content-cols prop-al-bg-dark">
        <div className="prop-al-container-main">
          <div className="prop-al-cols-row">
            <div className="prop-al-col" style={{ gridColumn: '1 / -1', position: 'relative' }}>
              <img src={cornerIcon} alt="" className="prop-corner-icon prop-corner-icon--tl" aria-hidden="true" />
              <EditableSlotGroup groupPath="petpolicy.pet_image" definition={PET_SLOT_DEFS['petpolicy.pet_image']}>
                <div className="prop-pp-hero-image">
                  <VibesitesImage url={petImage.image} alt={petImage.alt || ''} />
                </div>
              </EditableSlotGroup>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pet Policy Feature Card (dark bg, double-bordered decorative card) ── */}
      <section className="prop-pp-section">
        <div className="prop-container">
          <div className="prop-pp-card">
            <div className="prop-pp-card-inner">
              <div className="prop-pp-corner-decor" aria-hidden="true" />
              <div className="prop-pp-corner-decor prop-pp-corner-bottom" aria-hidden="true" />

              <RichTextContent path="petpolicy.section_title" label="Policy Section Title" defaultValue={contentSchema.tokens['petpolicy.section_title'].defaultValue} as="h2" className="prop-pp-title" />

              <div className="prop-pp-grid">
                {/* Column 1: Weight */}
                <div className="prop-pp-item">
                  <div className="prop-pp-icon">
                    <FontAwesomeIcon icon={faWeightScale} />
                  </div>
                  <EditableToken path="petpolicy.weight_limit" type="text" label="Weight Limit" defaultField={contentSchema.tokens['petpolicy.weight_limit'].defaultField} defaultValue={contentSchema.tokens['petpolicy.weight_limit'].defaultValue} as="p" className="prop-pp-text">
                    {formatPetPolicyValue(weightLimit, 'weight_limit')}
                  </EditableToken>
                </div>

                {/* Column 2: Pet count & breed restrictions */}
                <div className="prop-pp-item">
                  <div className="prop-pp-icon">
                    <FontAwesomeIcon icon={faShieldCat} />
                  </div>
                  <EditableToken path="petpolicy.max_pets" type="text" label="Max Pets" defaultField={contentSchema.tokens['petpolicy.max_pets'].defaultField} defaultValue={contentSchema.tokens['petpolicy.max_pets'].defaultValue} as="p" className="prop-pp-text">
                    {formatPetPolicyValue(maxPets, 'max_pets')}
                  </EditableToken>
                  {!/lbs/i.test(String(breedNote ?? '')) && (
                    <EditableToken path="petpolicy.breed_note" type="text" label="Breed Restrictions" defaultField={contentSchema.tokens['petpolicy.breed_note'].defaultField} defaultValue={contentSchema.tokens['petpolicy.breed_note'].defaultValue} as="p" className="prop-pp-text">
                      {breedNote}
                    </EditableToken>
                  )}
                </div>

                {/* Column 3: Fees & deposits — format raw numbers with $ and context */}
                <div className="prop-pp-item">
                  <div className="prop-pp-icon">
                    <FontAwesomeIcon icon={faMoneyCheckDollar} />
                  </div>
                  <EditableToken path="petpolicy.pet_rent" type="text" label="Pet Rent" defaultField={contentSchema.tokens['petpolicy.pet_rent'].defaultField} defaultValue={contentSchema.tokens['petpolicy.pet_rent'].defaultValue} as="p" className="prop-pp-text">
                    {petRentLine}
                  </EditableToken>
                  <EditableToken path="petpolicy.pet_fee" type="text" label="Pet Fee" defaultField={contentSchema.tokens['petpolicy.pet_fee'].defaultField} defaultValue={contentSchema.tokens['petpolicy.pet_fee'].defaultValue} as="p" className="prop-pp-text">
                    {petFeeLine}
                  </EditableToken>
                  {formatPetPolicyValue(petDeposit, 'pet_deposit') && (
                    <EditableToken path="petpolicy.pet_deposit" type="text" label="Pet Deposit" defaultField={contentSchema.tokens['petpolicy.pet_deposit'].defaultField} defaultValue={contentSchema.tokens['petpolicy.pet_deposit'].defaultValue} as="p" className="prop-pp-text">
                      {formatPetPolicyValue(petDeposit, 'pet_deposit')}
                    </EditableToken>
                  )}
                </div>
              </div>

              <div className="prop-pp-disclaimer">
                <RichTextContent
                  path="petpolicy.disclaimer"
                  label="Disclaimer"
                  defaultField={contentSchema.tokens['petpolicy.disclaimer'].defaultField}
                  defaultValue={contentSchema.tokens['petpolicy.disclaimer'].defaultValue}
                  as="div"
                />
                <p>
                  For any other questions regarding our pet policy, fill out a{' '}
                  <a href={`${basePath}/contact-us`}><strong>contact form</strong></a> or{' '}
                  <strong><a href={`tel:${phoneDigits(phone)}`}>give us a call</a></strong>.
                </p>
              </div>

              <RichTextContent
                path="petpolicy.additional_info"
                label="Additional Pet Policy Info"
                defaultField={contentSchema.tokens['petpolicy.additional_info'].defaultField}
                defaultValue={contentSchema.tokens['petpolicy.additional_info'].defaultValue}
                as="div"
                className="prop-pp-additional-info"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PetPolicy',
  displayName: 'Pet Policy Page',
  tokens: {
    'petpolicy.section_title': {
      label: 'Policy Section Title',
      type: 'richtext',
      defaultValue: '<span data-type="field-token" data-field="property_name"></span> Pet Policy',
    },
    'petpolicy.weight_limit': {
      label: 'Weight Limit',
      type: 'text',
      defaultField: 'property_max_pet_weight_limit',
      defaultValue: 'No weight limit',
    },
    'petpolicy.max_pets': {
      label: 'Max Pets',
      type: 'text',
      defaultField: 'property_max_pets',
      defaultValue: 'Maximum of two pets allowed',
    },
    'petpolicy.breed_note': {
      label: 'Breed Restrictions',
      type: 'text',
      defaultField: 'property_pet_restrictions',
      defaultValue: 'Breed restrictions apply',
    },
    'petpolicy.pet_rent': {
      label: 'Monthly Pet Rent',
      type: 'text',
      defaultField: 'property_monthly_pet_rent',
      defaultValue: 'Pet rent $30 (per pet per month)*',
    },
    'petpolicy.pet_fee': {
      label: 'Upfront Pet Fee',
      type: 'text',
      defaultField: 'property_upfront_pet_fee',
      defaultValue: '$500 fee per pet (non-refundable)*',
    },
    'petpolicy.pet_deposit': {
      label: 'Pet Deposit',
      type: 'text',
      defaultField: 'property_pet_deposit',
      defaultValue: '',
    },
    'petpolicy.disclaimer': {
      label: 'Disclaimer',
      type: 'richtext',
      defaultField: 'property_pet_policy_description',
      defaultValue: '*Service animals are not considered pets. Pet fees and pet rent are not applicable for service animals.',
    },
    'petpolicy.additional_info': {
      label: 'Additional Pet Policy Info',
      type: 'richtext',
      defaultField: 'property_petpolicy_info',
      defaultValue: '',
    },
  },
  slotGroups: {
    'petpolicy.pet_image': {
      label: 'Pet Section Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
      },
      sourceBinding: { collection: 'photo_gallery', defaultIndex: 0, fieldMappings: { image: 'image', alt: 'alt_text' } },
    },
  },
};

(PetPolicy as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PetPolicy;
